/**
 * Every file shared in the room, newest first.
 *
 * This is how somebody finds the spreadsheet a colleague posted last week, so
 * it is built around that: the month it landed in, who put it there, and a
 * filename filter that narrows as you type. Attachments carry no uploader of
 * their own, so the name is read from the message the file was posted with;
 * where that message has not been loaded the row simply says nothing rather
 * than guessing.
 *
 * The server answers a page at a time, and the filter searches what has been
 * loaded — so the button that fetches the next page stays reachable even when
 * nothing on screen matches, which is exactly when the file being looked for is
 * further back than the panel has read.
 */

import { useMemo, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'

import { api, isApiError } from '@/api/client'
import { useAttachments, useDeleteAttachment } from '@/api/queries'
import { useRoomMessages } from '@/api/store'
import type { Attachment, Room } from '@/api/types'
import { Badge, Button, IconButton, Modal, Spinner, TextField, useToast } from '@/components/ui'
import { formatBytes, formatDay } from '@/lib/format'
import panel from './DetailsPanel.module.css'

/**
 * Only a finalized file can be fetched. `uploaded` means the bytes landed but
 * nothing has vouched for them, and `scanning` means the verdict is still out;
 * the server refuses both, so offering the button would only produce an error.
 */
const DOWNLOADABLE: ReadonlySet<Attachment['status']> = new Set<Attachment['status']>(['ready'])

const STATUS_LABELS: Record<Attachment['status'], string> = {
  pending: 'Uploading',
  uploaded: 'Uploaded',
  scanning: 'Being scanned',
  ready: 'Ready',
  blocked: 'Blocked',
  deleted: 'Deleted',
}

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

/**
 * The endpoint itself rather than a signed URL: the browser carries the
 * session cookie, the server answers 302 to a fresh short-lived location, and
 * the link therefore cannot go stale while the panel sits open.
 */
function downloadHref(attachmentId: string): string {
  return `${api.getBasePath()}/attachments/${encodeURIComponent(attachmentId)}/download`
}

function monthKey(timestamp: string): string {
  return timestamp.slice(0, 7)
}

interface FileMonth {
  key: string
  label: string
  files: Attachment[]
}

function DownloadIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M8 2.5v8m0 0L5 7.6M8 10.5l3-2.9M3 12.5h10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3.5 4.5h9m-7 0v-1a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1m-6 0 .6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SearchIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <circle cx="7.2" cy="7.2" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.4 10.4 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export interface RoomFilesProps {
  room: Room
  canDelete: boolean
}

export function RoomFiles({ room, canDelete }: RoomFilesProps): ReactElement {
  const toast = useToast()
  const {
    data: attachments,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useAttachments(room.id)
  const { messages } = useRoomMessages(room.id)
  const deleteAttachment = useDeleteAttachment()

  const [query, setQuery] = useState('')
  const [pendingDeletion, setPendingDeletion] = useState<Attachment | null>(null)

  const uploaders = useMemo(() => {
    const byMessage = new Map<string, string>()
    for (const message of messages) {
      const name =
        message.sender_override ??
        (message.sender.display_name.trim() === ''
          ? message.sender.username
          : message.sender.display_name)
      byMessage.set(message.id, name)
    }
    return byMessage
  }, [messages])

  const months = useMemo<FileMonth[]>(() => {
    const needle = query.trim().toLocaleLowerCase()
    const visible = (attachments ?? [])
      .filter((attachment) => attachment.status !== 'deleted')
      .filter(
        (attachment) =>
          needle === '' || attachment.filename.toLocaleLowerCase().includes(needle),
      )
      .sort((left, right) => right.created_at.localeCompare(left.created_at))

    const format = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' })
    const ordered: FileMonth[] = []

    for (const attachment of visible) {
      const key = monthKey(attachment.created_at)
      const last = ordered[ordered.length - 1]
      if (last !== undefined && last.key === key) {
        last.files.push(attachment)
        continue
      }
      const parsed = new Date(attachment.created_at)
      const label = Number.isNaN(parsed.getTime()) ? key : format.format(parsed)
      ordered.push({ key, label, files: [attachment] })
    }

    return ordered
  }, [attachments, query])

  const total = months.reduce((count, month) => count + month.files.length, 0)

  const confirmDeletion = (): void => {
    const target = pendingDeletion
    if (target === null) return

    deleteAttachment.mutate(
      { roomId: room.id, attachmentId: target.id },
      {
        onSuccess: () => {
          toast.success(`${target.filename} was deleted.`)
          setPendingDeletion(null)
        },
        onError: (failure) =>
          toast.error(describe(failure, `Could not delete ${target.filename}.`)),
      },
    )
  }

  if (error !== null) {
    return (
      <p className={panel.error} role="alert">
        {describe(error, 'The file list could not be loaded.')}
      </p>
    )
  }

  if (isLoading || attachments === undefined) {
    return (
      <div className={panel.loading}>
        <Spinner label="Loading files" />
      </div>
    )
  }

  return (
    <div className={panel.stack}>
      <TextField
        label="Search files by name"
        labelHidden
        type="search"
        autoComplete="off"
        placeholder="Search files by name"
        leading={<SearchIcon />}
        value={query}
        onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
      />

      <p className="sr-only" aria-live="polite">
        {`${total} ${total === 1 ? 'file' : 'files'}${hasNextPage ? ', more not yet loaded' : ''}`}
      </p>

      {months.length === 0 ? (
        <p className={panel.empty}>
          {query.trim() === ''
            ? hasNextPage
              ? 'No files loaded yet.'
              : 'No files have been shared in this room yet.'
            : hasNextPage
              ? `No filename loaded so far matches “${query.trim()}”.`
              : `No filename matches “${query.trim()}”.`}
        </p>
      ) : (
        months.map((month) => (
          <section key={month.key} className={panel.section}>
            <h3 className={panel.groupTitle}>{month.label}</h3>
            <ul className={panel.list}>
              {month.files.map((attachment) => {
                const downloadable = DOWNLOADABLE.has(attachment.status)
                const uploader =
                  attachment.message_id === null
                    ? undefined
                    : uploaders.get(attachment.message_id)
                const meta = [
                  formatBytes(attachment.byte_size),
                  uploader,
                  formatDay(attachment.created_at),
                ].filter((part): part is string => part !== undefined && part !== '')

                return (
                  <li key={attachment.id} className={panel.row}>
                    <div className={panel.rowMain}>
                      <span className={panel.fileName}>
                        {downloadable ? (
                          <a
                            className={panel.fileLink}
                            href={downloadHref(attachment.id)}
                            download={attachment.filename}
                            rel="noopener"
                          >
                            {attachment.filename}
                          </a>
                        ) : (
                          <span className={panel.rowNameText}>{attachment.filename}</span>
                        )}
                        {downloadable ? null : (
                          <Badge tone={attachment.status === 'blocked' ? 'danger' : 'neutral'}>
                            {STATUS_LABELS[attachment.status]}
                          </Badge>
                        )}
                      </span>
                      <span className={panel.rowMeta}>{meta.join(' · ')}</span>
                    </div>

                    <div className={panel.rowActions}>
                      {downloadable ? (
                        <a
                          className={panel.iconLink}
                          href={downloadHref(attachment.id)}
                          download={attachment.filename}
                          rel="noopener"
                          aria-label={`Download ${attachment.filename}`}
                        >
                          <DownloadIcon />
                        </a>
                      ) : null}
                      {canDelete ? (
                        <IconButton
                          label={`Delete ${attachment.filename}`}
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingDeletion(attachment)}
                        >
                          <TrashIcon />
                        </IconButton>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ))
      )}

      {hasNextPage ? (
        <div className={panel.more}>
          <Button
            variant="secondary"
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            Load older files
          </Button>
        </div>
      ) : null}

      <Modal
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        title="Delete file"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingDeletion(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={deleteAttachment.isPending} onClick={confirmDeletion}>
              Delete
            </Button>
          </>
        }
      >
        <p className={panel.sectionHint}>
          {pendingDeletion === null
            ? ''
            : `${pendingDeletion.filename} will be removed from the server for everyone. The message it was posted with stays, without the file.`}
        </p>
      </Modal>
    </div>
  )
}

export default RoomFiles
